import {
  Alert,
  Box,
  Chip,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Link2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../common/components/Button";
import ConfirmationModal from "../../common/components/ConfirmationModal";
import PageHeader from "../../common/components/PageHeader";
import Pagination from "../../common/components/Pagination";
import { useProject } from "../../common/contexts/ProjectContext";
import { toast } from "../../common/components/Toast";
import { useAssets, useLinkEpisodes } from "../api/assets";
import AssetCard from "../components/AssetCard";
import DraggableAssetCard from "../components/DraggableAssetCard";
import DroppableSeriesCard from "../components/DroppableSeriesCard";
import { MissingAssetsPlaceholder } from "../components/MissingAssetsPlaceholder";

const Assets = () => {
  const { t } = useTranslation("assets");
  const navigate = useNavigate();
  const { currentUserRole } = useProject();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [assetTypeFilter, setAssetTypeFilter] = useState("");
  const isOwner = currentUserRole === "owner";
  const [pageSize, setPageSize] = useState(12);

  // Link Mode state
  const [linkMode, setLinkMode] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [pendingLink, setPendingLink] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showUnassigned, setShowUnassigned] = useState(false);

  const { assets, pagination, loading, refetch } = useAssets(
    currentPage,
    pageSize,
    searchTerm,
    assetTypeFilter,
    showUnassigned,
  );

  const { linkEpisodes, linking } = useLinkEpisodes();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // Reset to first page when search term or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, assetTypeFilter, showUnassigned]);

  // Calculate pagination from API response
  const totalItems = pagination.count;
  const totalPages = Math.ceil(totalItems / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const draggedAsset = assets.find((a) => a.id === active.id);
    const targetAsset = assets.find((a) => a.id === over.id);

    if (!targetAsset || !draggedAsset) return;
    if (targetAsset.asset_type !== "series") return;
    if (draggedAsset.asset_type === "series") return;

    setPendingLink({ series: targetAsset, episode: draggedAsset });
    setConfirmOpen(true);
  };

  const handleConfirmLink = async () => {
    if (!pendingLink) return;
    try {
      await linkEpisodes(pendingLink.series.id, [pendingLink.episode.id]);
      toast.success(t("linkMode.success"));
      refetch();
    } catch (error) {
      toast.error(t("linkMode.error"));
    } finally {
      setConfirmOpen(false);
      setPendingLink(null);
    }
  };

  const filterChips = [
    { label: t("filter.all"), value: "" },
    { label: t("filter.series"), value: "series" },
    { label: t("filter.standalone"), value: "standalone" },
  ];

  const searchBar = (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
    >
      {filterChips.map((chip) => (
        <Chip
          key={chip.value}
          label={chip.label}
          size="small"
          variant={
            assetTypeFilter === chip.value && !showUnassigned
              ? "filled"
              : "outlined"
          }
          color={
            assetTypeFilter === chip.value && !showUnassigned
              ? "primary"
              : "default"
          }
          onClick={() => {
            setAssetTypeFilter(chip.value);
            setShowUnassigned(false);
          }}
        />
      ))}
      {isOwner && (
        <Chip
          label={t("filter.unassigned")}
          size="small"
          variant={showUnassigned ? "filled" : "outlined"}
          color={showUnassigned ? "warning" : "default"}
          onClick={() => {
            setShowUnassigned(!showUnassigned);
            if (!showUnassigned) setAssetTypeFilter("");
          }}
        />
      )}
      <TextField
        size="small"
        placeholder={t("searchPlaceholder")}
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={20} />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: { xs: 0, sm: 250 },
          "& .MuiOutlinedInput-root": {
            backgroundColor: "background.paper",
          },
        }}
      />
    </Box>
  );

  const renderGrid = () => (
    <Grid container spacing={3}>
      {assets.map((asset) =>
        linkMode ? (
          asset.asset_type === "series" ? (
            <DroppableSeriesCard asset={asset} key={asset.id} />
          ) : (
            <DraggableAssetCard asset={asset} key={asset.id} />
          )
        ) : (
          <AssetCard asset={asset} key={asset.id} />
        ),
      )}
    </Grid>
  );

  return (
    <>
      <PageHeader
        title={t("title")}
        description={t("description")}
        appendActions={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {isOwner && (
              <Button
                variant={linkMode ? "contained" : "secondary"}
                size="small"
                startIcon={<Link2 size={16} />}
                onClick={() => setLinkMode(!linkMode)}
              >
                {linkMode ? t("linkMode.active") : t("linkMode.toggle")}
              </Button>
            )}
            {isOwner && (
              <Button
                variant="secondary"
                size="small"
                onClick={() => navigate("/assets/unlinked-episodes")}
              >
                {t("manageEpisodes")}
              </Button>
            )}
            {searchBar}
          </Box>
        }
      />

      {linkMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("linkMode.instructions")}
        </Alert>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : totalItems > 0 || searchTerm ? (
        <>
          {assets?.length === 0 && searchTerm ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "200px",
              }}
            >
              <Search size={60} />
              <Typography sx={{ mt: 1, color: "text.secondary" }}>
                {t("noSearchResults", { searchTerm })}
              </Typography>
            </Box>
          ) : linkMode ? (
            <>
              <DndContext
                sensors={sensors}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                {renderGrid()}
                <DragOverlay>
                  {activeId ? (
                    <Box sx={{ width: 300, opacity: 0.9 }}>
                      <AssetCard
                        asset={assets.find((a) => a.id === activeId)}
                        isWrapped
                      />
                    </Box>
                  ) : null}
                </DragOverlay>
              </DndContext>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setCurrentPage(1);
                  }}
                />
              )}
            </>
          ) : (
            <>
              {renderGrid()}

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setCurrentPage(1);
                  }}
                />
              )}
            </>
          )}
        </>
      ) : (
        <MissingAssetsPlaceholder />
      )}

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setPendingLink(null);
        }}
        onConfirm={handleConfirmLink}
        title={t("linkMode.confirmTitle")}
        message={t("linkMode.confirmMessage", {
          episode: pendingLink?.episode?.title || "",
          series: pendingLink?.series?.title || "",
        })}
        confirmText={t("linkMode.confirmButton")}
        confirmColor="primary"
        loading={linking}
      />
    </>
  );
};

export default Assets;
